import {isUndefined} from 'lodash';
import React, {FC} from 'react';

import {createMandatoryContext} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {computeIsGlobalNamespace} from '../../../helpers/models/namespaceHelpers';
import {RuleModelStrict} from '../../../models/api/ruleModel';
import {RuleRecipe} from '../../rules/library/recipes';
import {RuleIntegration} from '../../rules/ruleIntegrationModel';
import {BaseEditorContext, createBaseEditorContext, CreateBaseEditorContextProps} from '../baseEditorContext';
import {
  CreateExpressionContextProps,
  createExpressionsContext,
  ExpressionsContext,
} from '../expressions/expressionsContext';
import {ActivityResult} from '../expressions/infer/expressionResultModel';

/*
 * Types.
 */

interface RuleEditorBaseContext {
  recipe?: RuleRecipe;
  recipeRule?: RuleRecipe['rule'];
  isLibrary: boolean;
  isCustomView: boolean;
  ruleIntegration: RuleIntegration;
}

export interface RuleEditorContext extends BaseEditorContext, ExpressionsContext, RuleEditorBaseContext {}

// Omitting `initialInfer` from `CreateExpressionContextProps` as we'll use `ActivityResult` in the rule editor.
interface CreateRuleEditorContextProps
  extends CreateBaseEditorContextProps,
    Omit<CreateExpressionContextProps, 'initialInfer'> {
  rule?: RuleModelStrict;
  recipe?: RuleRecipe;
  isLibrary?: boolean;
  ruleIntegration: RuleIntegration;
}

/*
 * Context.
 */

export const [RuleEditorBaseContext, useRuleEditorContext] = createMandatoryContext<RuleEditorBaseContext>();

export const RuleEditorContextProvider: FC<{context: RuleEditorContext}> = ({context, children}) => (
  <BaseEditorContext.Provider value={context}>
    <ExpressionsContext.Provider value={context}>
      <RuleEditorBaseContext.Provider value={context}>{children}</RuleEditorBaseContext.Provider>
    </ExpressionsContext.Provider>
  </BaseEditorContext.Provider>
);

export const createRuleEditorContext = ({
  rule,
  recipe,
  isLibrary,
  ruleIntegration,
  namespace,
  hasExpressions,
  hasHighlightErrorsFlag,
  expressions,
  customFields,
  setPanelOpen,
  expressionCreationState,
  setExpressionCreationState,
}: CreateRuleEditorContextProps): RuleEditorContext => {
  const isReadOnly =
    rule &&
    rule.bundle &&
    computeIsGlobalNamespace(rule.bundle.namespace) &&
    !computeIsGlobalNamespace(namespace);

  const isEditing = !isUndefined(rule);

  const baseEditorContext = createBaseEditorContext({
    namespace,
    isEditing,
    isReadOnly,
    hasExpressions,
    hasHighlightErrorsFlag,
  });

  const isLibraryRecipe = Boolean(recipe) || Boolean(isLibrary);

  const expressionsContext = createExpressionsContext({
    expressions,
    customFields,
    initialInfer: ActivityResult,
    setPanelOpen,
    expressionCreationState,
    setExpressionCreationState,
  });

  return {
    ...baseEditorContext,
    ...expressionsContext,
    recipe,
    recipeRule: recipe?.rule,
    isLibrary: isLibraryRecipe,
    isCustomView: false, // @TODO: recipeType === RuleRecipeTypeEnum.CUSTOM_VIEW,
    ruleIntegration,
  };
};
